import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { RadioButton } from 'primereact/radiobutton';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import customerService from '../customer/customer-service';
import { Fieldset } from 'primereact/fieldset';
import { Calendar } from 'primereact/calendar';
import tenantService from './tenant-service';
import { InputSwitch } from 'primereact/inputswitch';
import BreadcrumbComponent from '../../../components/Breadcrumb';

interface DropdownItem {
    name: string;
    code: string;
    facilityCode: string;
    fullName: string;
}

const TenantEdit: React.FC = () => {

    const toast = useRef<Toast>(null);
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const [formData, setFormData] = useState<any>({});
    const [clientState, setClientState] = useState<any>(null);
    const [stateItems, setStateItems] = useState<DropdownItem[]>([]);
    const [countryItems, setCountryItems] = useState<DropdownItem[]>([]);
    const [districtItems, setDistrictItems] = useState<DropdownItem[]>([]);
    const [postcodeItems, setPostcodeItems] = useState<DropdownItem[]>([]);
    
    

    const dropdownStateItems = useMemo(() => stateItems, [stateItems]);
    const dropdownCountryItems = useMemo(() => countryItems, [countryItems]);
    const dropdownDistrictItems = useMemo(() => districtItems, [districtItems]);
    const dropdownPostcodeItems = useMemo(() => postcodeItems, [postcodeItems]);


    const tenant = location.state?.tenant;
    const isEditMode = !!tenant;

    const currentDate = new Date().toLocaleString("en-US", { timeZone: clientState?.timeZone });

    useEffect(() => {
        const fetchClientState = async () => {
            try {
                const clientStateItem = await tenantService.getClientState();
                setClientState(clientStateItem);

                const stateData = await tenantService.getStateDropdown('MYS');
                setStateItems(stateData);

                const countryData = await tenantService.getCountryDropdown();
                setCountryItems(countryData);
                

                if (isEditMode) {
                    setFormData(tenant)

                    if (tenant) {
                        const districtData = await customerService.getDistrictDropdown(tenant.stateCode);
                        setDistrictItems(districtData);

                        const postcodeData = await customerService.getPostcodeDropdown(tenant.stateCode);
                        setPostcodeItems(postcodeData);
                    }
                }

            } catch (error) {
                console.error('Error fetching client state:', error);
            }
        };

        fetchClientState();
    }, []);


    const handleInputChange = (e: any) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value
        });
    };

    const handleDropdownChange = (e: any) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value
        });
    };

    const handleStateDropdownChange = async (e: any) => {
        const stateCode = e.value;

        if (stateCode) {
            try {
                const districtData = await customerService.getDistrictDropdown(stateCode);
                setDistrictItems(districtData);

                const postcodeData = await customerService.getPostcodeDropdown(stateCode);
                setPostcodeItems(postcodeData);
            } catch (error) {
                console.error('Error fetching district data:', error);
            }
        }

        setFormData({
            ...formData,
            stateCode: e.value
        });

    };

    const formatDateToDisplay = (dateString: string | null | undefined) => {
        if (!dateString) return null; // Handle null or undefined
        return new Date(dateString); // Ensure it's a valid Date object
    };

    const validate = () => {

    }

    function handleBack(): void {
        navigate(`/admin/tenant`);
    }

    const handleSave = async (event: React.FormEvent) => {
        event.preventDefault();

        setLoading(true);

        formData.recordStatus = formData.recordStatus == true ? '1' : '0';

        if (isEditMode) {
            formData.modifiedBy = clientState.userId;
            formData.modifiedDate = currentDate;
        } else {
            formData.insertBy = clientState.userId;
            formData.insertDate = currentDate;
            formData.modifiedBy = clientState.userId;
            formData.modifiedDate = currentDate;
        }
        if (isEditMode) {

            try {
                tenantService.update(formData.facilityCode, formData);
                await new Promise((resolve) => setTimeout(resolve, 2000));
                alert('Form submitted successfully!');
                navigate(`/admin/tenant`);
            } catch (error) {
                console.error("Error with Axios:", error);
                alert('Failed to submit the form.');
            } finally {
                setLoading(false);
            }

        }
    }

    const toolbarTemplate = () => {
        return (
            <>
                <Toast ref={toast} />
                <Button label="Save" icon="pi pi-check" style={{ marginRight: '.5em' }} onClick={handleSave} />
                {/* <Button label="Delete" icon="pi pi-trash" style={{ marginRight: '.5em' }} /> */}
                <Button label="Back" icon="pi pi-times" style={{ marginRight: '.5em' }} onClick={() => handleBack()} />

            </>
        );
    };

    return (
        <div className='grid'>
            <div className='col-12'>
                <div className='card-title'>
                    <h5>Facility Form</h5>
                    <BreadcrumbComponent />
                </div>
                <div className='form-title'>
                    <Fieldset legend="Facility Information">
                        <div className="p-fluid form-grid grid">

                            <div className="field col-12 md:col-6">
                                <label>Start Live Date</label>
                                <Calendar
                                    value={formatDateToDisplay(formData.liveDate) || ""}
                                    dateFormat="dd MM yy"
                                    showIcon
                                    showTime
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Facility Code</label>
                                <InputText
                                    id="facilityCode"
                                    type="text"
                                    value={formData.facilityCode}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Facility Full Name</label>
                                <InputText
                                    id="fullName"
                                    type="text"
                                    value={formData.fullName}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Faciltiy Short Name</label>
                                <InputText
                                    id="shortName"
                                    type="text"
                                    value={formData.shortName}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <h6>Record Status</h6>
                                <InputSwitch
                                    checked={formData.recordStatus === "1"}
                                    onChange={(e) => setFormData({ ...formData, recordStatus: e.value })}
                                />
                            </div>
                        </div>
                    </Fieldset>
                    <div>
                        <br />
                    </div>
                    <Fieldset legend="Contact Information" toggleable>
                        <div className="p-fluid form-grid grid">
                            <div className="field col-12">
                                <label>Address</label>
                                <InputText
                                    id="address"
                                    type="text"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Country</label>
                                <Dropdown
                                    id="countryCode"
                                    value={formData.countryCode}
                                    onChange={handleDropdownChange}
                                    options={dropdownCountryItems}
                                    optionLabel="fullDesc"
                                    placeholder="Select One"
                                    optionValue="countryCode"
                                >
                                </Dropdown>
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>State</label>
                                <Dropdown
                                    id="stateCode"
                                    value={formData.stateCode}
                                    onChange={handleStateDropdownChange}
                                    options={dropdownStateItems}
                                    optionLabel="fullDesc"
                                    placeholder="Select One"
                                    optionValue="stateCode"
                                >
                                </Dropdown>
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Postcode</label>
                                <Dropdown
                                    id="postcode"
                                    value={formData.postcode}
                                    onChange={handleDropdownChange}
                                    options={dropdownPostcodeItems}
                                    optionLabel="postcode"
                                    placeholder="Select One"
                                    optionValue="postcode"
                                >
                                </Dropdown>
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>District</label>
                                <Dropdown
                                    id="districtCode"
                                    value={formData.districtCode}
                                    onChange={handleDropdownChange}
                                    options={dropdownDistrictItems}
                                    optionLabel="fullDesc"
                                    placeholder="Select One"
                                    optionValue="districtCode"
                                >
                                </Dropdown>
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>City</label>
                                <InputText
                                    id="city"
                                    type="text"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                />
                            </div>


                            <div className="field col-12 md:col-6">
                                <label>Telephone No.</label>
                                <InputText
                                    id="telephoneNo"
                                    type="text"
                                    value={formData.telephoneNo}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Fax No.</label>
                                <InputText
                                    id="faxNo"
                                    type="text"
                                    value={formData.faxNo}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Email</label>
                                <InputText
                                    id="email"
                                    type="text"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                    </Fieldset>
                    <div>
                        <br />
                    </div>

                    <Toolbar end={toolbarTemplate}></Toolbar>
                </div>
            </div>

        </div>
    );
};

export default TenantEdit;
